'use client'

import { CookieNameEnum } from "@/src/constants/cookie-names"
import Merchant from "@/src/interfaces/merchant/merchant"
import MerchantPolling from "@/src/interfaces/polling/merchant"
import { getCookie } from "cookies-next"
import { createContext, ReactNode, useContext, useEffect, useState } from "react"
import { MERCHANT_DATA_EXPIRATION_MINUTES, MERCHANT_STORE_NAME } from "@/src/constants/defaults"
import PathHelper from "@/src/helpers/path-helper"
import { useAppAccessToken } from "../app-access-token/app-access-token-context"
import MerchantDb from "@/src/core/merchant-db"
import { decryptClient } from "@/src/app/api/encryption/encryption-client"
import { usePathname } from "next/navigation"
import MerchantContextFunctions from "./merchant-functions"
import { runMiddleware } from "@/src/functions/gereric"

export interface MerchantDetailedProps {
  isLoading?: boolean
  model?: Merchant.Response
  error?: string
}

export interface MerchantPollingProps {
  isLoading?: boolean
  model?: MerchantPolling.StatusResponse
}

interface MerchantContextType {
  merchantDetailed: MerchantDetailedProps | null
  setMerchantDetailed: (merchant: MerchantDetailedProps) => void
  merchantSummary: Merchant.ResponseSummary | null
  setMerchantSummary: (merchant: Merchant.ResponseSummary) => void
  polling: MerchantPollingProps | null
  localMode: boolean | undefined
  setLocalMode: (e: boolean) => void
  isAppAndroidOrIOS: boolean | undefined
  fetchPolling: (skipCache?: boolean) => Promise<MerchantPolling.StatusResponse | undefined>
}

const MerchantContext = createContext<MerchantContextType | undefined>(undefined)

const KEY_PATH = 'merchant_details'

export const MerchantProvider = ({ children }: { children: ReactNode }) => {
  const { appAccessToken } = useAppAccessToken()

  const [merchantSummary, setMerchantSummary] = useState<Merchant.ResponseSummary | null>(null)
  const [merchantDetailed, setMerchantDetailed] = useState<MerchantDetailedProps | null>(null)
  const [polling, setPolling] = useState<MerchantPollingProps | null>({ isLoading: true })
  const [localMode, setLocalMode] = useState<boolean>()
  const [isAppAndroidOrIOS, setIsAppAndroidOrIOS] = useState<boolean>()
  const [isMounted, setIsMounted] = useState(false)

  const pathname = usePathname()

  const fetchPolling = async (skipCache?: boolean) => {
    return await MerchantContextFunctions.fetchPolling({
      polling,
      merchantId: merchantSummary?.id!,
      appAccessToken: appAccessToken!,
      merchantDetailed,
      skipCache
    })
  }

  const saveMerchant = async (storedMerchant: any, encryptedModel: any) => {
    const db = await MerchantDb.getDB()

    const currentDate = Date.now()
    let expiresAt = storedMerchant?.expiresAt ?? 0

    if (currentDate > expiresAt)
      expiresAt = Date.now() + (60 * MERCHANT_DATA_EXPIRATION_MINUTES * 1000)

    if (db) {
      await db.put(MERCHANT_STORE_NAME, {
        id: KEY_PATH,
        data: encryptedModel,
        expiresAt: expiresAt
      })
    }
  }

  useEffect(() => {
    const isAppAndroidOrIOS = !!getCookie('isAppAndroidOrIOS')
    setIsAppAndroidOrIOS(isAppAndroidOrIOS)
  }, [])

  useEffect(() => {
    const getPolling = async () => {
      const pollingResponse = await fetchPolling()

      if (pollingResponse)
        setPolling({ model: pollingResponse, isLoading: false })
    }

    if (merchantSummary)
      getPolling()
  }, [merchantDetailed, merchantSummary])

  useEffect(() => {
    const summaryCookie = getCookie(CookieNameEnum.MerchantSummary)

    if (merchantSummary && summaryCookie === JSON.stringify(merchantSummary))
      return

    if (summaryCookie && isMounted)
      setMerchantSummary(JSON.parse(summaryCookie) as Merchant.ResponseSummary)

    if (!summaryCookie && isMounted) {
      const updateCookie = async () => {
        const response = await runMiddleware()

        if (response.ok) {
          const newSummaryCookie = getCookie(CookieNameEnum.MerchantSummary)

          if (newSummaryCookie)
            setMerchantSummary(JSON.parse(newSummaryCookie) as Merchant.ResponseSummary)
        }
      }

      updateCookie()
    }
  }, [merchantSummary, pathname, isMounted])

  useEffect(() => {
    const fetchMerchantDetailed = async () => {
      try {
        setMerchantDetailed({ isLoading: true })

        const db = await MerchantDb.getDB()

        const storedMerchant = db ? await db.get(MERCHANT_STORE_NAME, KEY_PATH) : undefined

        let storedMerchantDecriptResponse: any = storedMerchant

        const isModelEncrypted = !!storedMerchant?.data
        let skipValidation = false

        if (isModelEncrypted && storedMerchant) {
          try {
            storedMerchantDecriptResponse = decryptClient(storedMerchant.data)
          }
          catch {
            skipValidation = true
          }
        }

        if (!skipValidation) {
          const storedMerchantDecriptedData = !isModelEncrypted ? storedMerchant : JSON.parse(storedMerchantDecriptResponse)

          if (storedMerchantDecriptedData) {
            const currentDate = Date.now()
            const expiresAt = storedMerchant?.expiresAt ?? 0

            if (currentDate < expiresAt) {
              setMerchantDetailed({ model: storedMerchantDecriptedData, isLoading: false })
              return
            }

            if (db)
              await db.delete(MERCHANT_STORE_NAME, KEY_PATH)
          }
        }

        const params: Merchant.MerchantSearchParams = {
          url: PathHelper.getDomain(),
          viewMode: 'Detailed',
        }

        const merchantEncriptResponse = await fetch('/api/merchant', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            params: params,
            appAccessToken: appAccessToken
          }),
          cache: 'no-store'
        })

        if (!merchantEncriptResponse.ok)
          throw Error(merchantEncriptResponse.statusText)

        const encryptedMerchantData = await merchantEncriptResponse.json()

        const decriptResponse = decryptClient(encryptedMerchantData)

        const decryptedMerchantData = JSON.parse(decriptResponse) as Merchant.Response

        setMerchantDetailed({ model: decryptedMerchantData, isLoading: false })

        saveMerchant(storedMerchant, encryptedMerchantData)
      }
      catch (e) {
        const msg = 'Erro ao obter os detalhes do estabelecimento.'
        setMerchantDetailed({ error: msg, isLoading: false })
        console.error(msg, e)
      }
    }

    if (appAccessToken && isMounted)
      fetchMerchantDetailed()
  }, [appAccessToken, isMounted])

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <MerchantContext.Provider
      value={{
        merchantDetailed, setMerchantDetailed,
        merchantSummary, setMerchantSummary,
        polling,
        localMode, setLocalMode,
        isAppAndroidOrIOS,
        fetchPolling
      }}
    >
      {children}
    </MerchantContext.Provider>
  )
}

export const useMerchant = (): MerchantContextType => {
  const context = useContext(MerchantContext)
  if (!context)
    throw new Error('O contexto de Merchant deve estar dentro do MerchantProvider.')
  return context
}
